import { useState } from "react";

/**
 * IntakeAnswersForm — client-side widget rendered right before payment.
 *
 * Given a service's `intakeQuestions` array, renders the appropriate
 * input for each type and manages the answers dictionary via
 * `value` / `onChange`. Validates required fields locally so we don't
 * ever bother Stripe with a payload we know is going to bounce.
 *
 * Value shape: { [questionId]: string | string[] | number }
 */
export default function IntakeAnswersForm({ questions, value, onChange, testIdPrefix = "intake-answer" }) {
  const answers = value || {};
  const setAns = (qid, v) => onChange({ ...answers, [qid]: v });

  const toggleMulti = (qid, opt) => {
    const cur = Array.isArray(answers[qid]) ? answers[qid] : [];
    const next = cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt];
    setAns(qid, next);
  };

  if (!questions?.length) return null;

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-form`}>
      {questions.map((q) => {
        const qid = q.id;
        const common = "w-full bg-input border border-secondary/25 p-2.5 rounded-sm text-sm";
        return (
          <div key={qid} className="space-y-1.5" data-testid={`${testIdPrefix}-field-${qid}`}>
            <label className="block text-xs uppercase tracking-[0.15em] text-foreground/70">
              {q.label}
              {q.required && <span className="text-destructive ml-1">*</span>}
            </label>
            {q.type === "text" && (
              <input
                value={answers[qid] || ""}
                onChange={(e) => setAns(qid, e.target.value)}
                className={common}
                data-testid={`${testIdPrefix}-input-${qid}`}
                maxLength={200}
              />
            )}
            {q.type === "textarea" && (
              <textarea
                value={answers[qid] || ""}
                onChange={(e) => setAns(qid, e.target.value)}
                rows={3}
                className={common}
                data-testid={`${testIdPrefix}-input-${qid}`}
                maxLength={2000}
              />
            )}
            {q.type === "number" && (
              <input
                type="number"
                value={answers[qid] ?? ""}
                onChange={(e) => setAns(qid, e.target.value)}
                className={common}
                data-testid={`${testIdPrefix}-input-${qid}`}
              />
            )}
            {q.type === "date" && (
              <input
                type="date"
                value={answers[qid] || ""}
                onChange={(e) => setAns(qid, e.target.value)}
                className={common}
                data-testid={`${testIdPrefix}-input-${qid}`}
              />
            )}
            {q.type === "select" && (
              <select
                value={answers[qid] || ""}
                onChange={(e) => setAns(qid, e.target.value)}
                className={common}
                data-testid={`${testIdPrefix}-input-${qid}`}
              >
                <option value="">— Choose one —</option>
                {(q.options || []).filter(Boolean).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}
            {q.type === "multiselect" && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid={`${testIdPrefix}-input-${qid}`}>
                {(q.options || []).filter(Boolean).map((opt) => {
                  const on = Array.isArray(answers[qid]) && answers[qid].includes(opt);
                  return (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => toggleMulti(qid, opt)}
                      className={`px-3 py-1.5 rounded-sm text-xs border transition ${
                        on
                          ? "bg-secondary text-secondary-foreground border-secondary"
                          : "border-secondary/30 hover:border-secondary/60"
                      }`}
                      data-testid={`${testIdPrefix}-option-${qid}-${opt}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
            {q.help && (
              <div className="text-[11px] text-foreground/50">{q.help}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Validate answers against a question schema. Returns an array of
 * missing-required labels; empty array means everything's good.
 */
export function validateIntakeAnswers(questions, answers) {
  if (!Array.isArray(questions)) return [];
  const a = answers || {};
  const missing = [];
  for (const q of questions) {
    if (!q.required) continue;
    const v = a[q.id];
    if (v === undefined || v === null) { missing.push(q.label || q.id); continue; }
    if (typeof v === "string" && !v.trim()) { missing.push(q.label || q.id); continue; }
    if (Array.isArray(v) && v.length === 0) { missing.push(q.label || q.id); continue; }
  }
  return missing;
}
